<template>
  <div class="main-panel">
    <div class="nav-bar-operations">
      <img
        src="@/assets/logogruas.png"
        alt="HomePage"
        class="logo-button"
        @click="backMainPanel"
      />
    </div>

    <header class="header">
      <div class="header__left">
        <div class="avatar" aria-hidden="true">N</div>
        <div class="greet">
          <h1>Importar archivos de <span>Nomina</span></h1>
          <p class="sub">Sigue los pasos para subir el listado de nomina</p>
        </div>
      </div>
      <button class="back-btn" @click="goBackToMenu">Volver a Nomina</button>
    </header>

    <section class="wizard">
      <!-- Step 1: Week -->
      <div class="step-card">
        <div class="step-head">
          <span class="step-number">1</span>
          <h2>Selecciona la semana</h2>
        </div>
        <div class="step-body row">
          <label class="field">
            Ano
            <input type="number" v-model.number="selectedYear" min="2000" max="2100" />
          </label>
          <label class="field grow">
            Semana (Nomina)
            <select v-model="selectedPayrollWeekKey">
              <option v-for="week in payrollWeeks" :key="week.key" :value="week.key">
                {{ payrollWeekLabel(week) }}
              </option>
            </select>
          </label>
        </div>
      </div>

      <!-- Step 2: Company -->
      <div class="step-card">
        <div class="step-head">
          <span class="step-number">2</span>
          <h2>Selecciona la empresa</h2>
        </div>
        <div class="step-body">
          <div class="company-grid">
            <button
              v-for="company in companies"
              :key="company"
              type="button"
              class="company-btn"
              :class="{ 'company-btn--active': selectedCompany === company }"
              @click="selectedCompany = company"
            >
              {{ company }}
            </button>
          </div>
        </div>
      </div>

      <!-- Step 3: File -->
      <div class="step-card" :class="{ 'step-card--disabled': !weekAndCompanySelected }">
        <div class="step-head">
          <span class="step-number">3</span>
          <h2>Sube el archivo</h2>
        </div>
        <div class="step-body">
          <p class="hint" v-if="!weekAndCompanySelected">
            Selecciona primero la semana y la empresa para habilitar la carga.
          </p>

          <p class="hint" v-else-if="checkingExisting">Verificando archivos cargados...</p>

          <!-- A file already exists for this week + company -->
          <div class="existing-box" v-else-if="existingImport">
            <p>
              <strong>Ya existe un archivo cargado</strong> para esta empresa y semana.
              Para subir uno nuevo, primero elimina el actual.
            </p>
            <ul class="existing-meta">
              <li v-if="existingImport.original_filename">
                Archivo: <strong>{{ existingImport.original_filename }}</strong>
              </li>
              <li>Empleados: <strong>{{ existingImport.row_count }}</strong></li>
              <li v-if="existingImport.uploaded_by">
                Subido por: <strong>{{ existingImport.uploaded_by }}</strong>
              </li>
              <li v-if="existingImport.created_at">
                Fecha: <strong>{{ formatDateTime(existingImport.created_at) }}</strong>
              </li>
            </ul>
            <div class="existing-actions">
              <button class="preview-btn" @click="openPreview" :disabled="previewLoading">
                {{ previewLoading ? 'Cargando...' : 'Previsualizar' }}
              </button>
              <button class="delete-btn" @click="deleteImport" :disabled="deleting">
                {{ deleting ? 'Eliminando...' : 'Eliminar archivo' }}
              </button>
            </div>
          </div>

          <!-- No file yet: allow upload -->
          <div class="file-row" v-else>
            <input
              ref="fileInput"
              type="file"
              accept=".xlsx,.csv"
              @change="onFileChange"
            />
            <p class="hint">
              Formatos permitidos: <strong>.xlsx</strong> y <strong>.csv</strong>.
              Los archivos <strong>.xls</strong> (Excel antiguo) deben guardarse como .xlsx o .csv.
            </p>
            <p class="selected-file" v-if="selectedFile">
              Archivo seleccionado: <strong>{{ selectedFile.name }}</strong>
            </p>
          </div>
        </div>
      </div>

      <section v-if="errorMessage" class="error-box">
        <strong>El archivo fue rechazado.</strong>
        <p>{{ errorMessage }}</p>
        <ul v-if="missingColumns.length">
          <li v-for="col in missingColumns" :key="col">{{ col }}</li>
        </ul>
      </section>

      <section v-if="result" class="success-box">
        <strong>Archivo importado correctamente.</strong>
        <p>
          Empresa: <strong>{{ result.company }}</strong> &mdash; Semana
          <strong>{{ result.week_index }}</strong>
          ({{ formatShortDate(result.week_start_date) }} - {{ formatShortDate(result.week_end_date) }})
        </p>
        <p>
          Empleados detectados: <strong>{{ result.row_count }}</strong>
          <span v-if="result.replaced"> (se reemplazo una importacion previa)</span>
        </p>
        <p v-if="!result.file_stored" class="warn">
          Nota: los datos se guardaron, pero el archivo original no pudo respaldarse en el almacenamiento.
          <span v-if="result.file_error"><br />Detalle: {{ result.file_error }}</span>
        </p>
        <details v-if="result.columns_detected && result.columns_detected.length">
          <summary>Columnas detectadas ({{ result.columns_detected.length }})</summary>
          <div class="chips">
            <span class="data-chip" v-for="col in result.columns_detected" :key="col">{{ col }}</span>
          </div>
        </details>
        <div class="preview-wrap" v-if="result.preview && result.preview.length">
          <table class="preview-table">
            <thead>
              <tr>
                <th>Empleado</th>
                <th>Nombre</th>
                <th>Puesto</th>
                <th>Departamento</th>
                <th>Percepciones</th>
                <th>Retenciones</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in result.preview" :key="i">
                <td>{{ row.employee_number }}</td>
                <td>{{ row.employee_name }}</td>
                <td>{{ row.puesto }}</td>
                <td>{{ row.departamento }}</td>
                <td>{{ row.percepciones }}</td>
                <td>{{ row.retenciones }}</td>
                <td>{{ row.total }}</td>
              </tr>
            </tbody>
          </table>
          <p class="hint">Mostrando los primeros {{ result.preview.length }} registros.</p>
        </div>
      </section>

      <div class="actions" v-if="!existingImport">
        <button
          class="submit-btn"
          :disabled="!canSubmit || uploading"
          @click="submit"
        >
          {{ uploading ? 'Validando...' : 'Validar y guardar' }}
        </button>
      </div>
    </section>

    <!-- Preview modal -->
    <div class="modal-overlay" v-if="showPreview" @click.self="closePreview">
      <div class="modal">
        <div class="modal-head">
          <h3>
            Vista previa
            <span v-if="previewData">&mdash; {{ previewData.original_filename || previewData.company }}</span>
          </h3>
          <button class="modal-close" @click="closePreview">&times;</button>
        </div>
        <div class="modal-body">
          <p class="hint" v-if="previewData">
            {{ previewData.company }} &mdash; Semana {{ previewData.week_index }} &middot;
            {{ previewData.row_count }} empleados
          </p>
          <div class="preview-wrap" v-if="previewData && previewData.rows && previewData.rows.length">
            <table class="preview-table">
              <thead>
                <tr>
                  <th v-for="(col, i) in previewData.columns" :key="i">{{ col }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, ri) in previewData.rows" :key="ri">
                  <td v-for="(col, ci) in previewData.columns" :key="ci">{{ formatCell(row[col]) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="hint" v-else>No hay datos para mostrar.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'RHNominaImport',
  data() {
    const now = new Date()
    return {
      permissions: [],
      selectedYear: now.getFullYear(),
      selectedPayrollWeekKey: '',
      selectedCompany: '',
      companies: ['Gruas Monterrey', 'Gruas Saltillo', 'Metrogruas', 'Gruas Alfa'],
      selectedFile: null,
      uploading: false,
      errorMessage: '',
      missingColumns: [],
      result: null,
      existingImport: null,
      checkingExisting: false,
      deleting: false,
      previewLoading: false,
      showPreview: false,
      previewData: null,
    }
  },
  computed: {
    payrollWeeks() {
      return this.buildPayrollWeeks(this.selectedYear)
    },
    selectedPayrollWeek() {
      return this.payrollWeeks.find((week) => week.key === this.selectedPayrollWeekKey) || null
    },
    weekAndCompanySelected() {
      return Boolean(this.selectedPayrollWeek && this.selectedCompany)
    },
    canSelectFile() {
      return Boolean(this.weekAndCompanySelected && !this.existingImport && !this.checkingExisting)
    },
    canSubmit() {
      return Boolean(this.canSelectFile && this.selectedFile)
    },
  },
  watch: {
    selectedYear() {
      this.syncWeekSelectionForYear()
    },
    selectedPayrollWeekKey() {
      this.fetchExistingImport()
    },
    selectedCompany() {
      this.fetchExistingImport()
    },
  },
  created() {
    this.fetchCurrentUser()
  },
  methods: {
    hasPerm(permOrPerms) {
      if (!permOrPerms) return false
      const perms = Array.isArray(permOrPerms) ? permOrPerms : [permOrPerms]
      return perms.some((perm) => {
        if (this.permissions.includes(perm)) return true
        return this.permissions.includes(`access_control.${perm}`)
      })
    },
    hasAllPerms(perms) {
      return Array.isArray(perms) && perms.length > 0 && perms.every((perm) => this.hasPerm(perm))
    },
    async fetchCurrentUser() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const { data } = await axios.get(`${apiDirectory}/current-user/`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        })
        this.permissions = data.permissions || []
        if (!this.hasAllPerms(['ui_access_rrhh_nomina', 'ui_access_rrhh_module'])) {
          this.$router.push({ name: 'MainPanel' })
          return
        }
        this.syncWeekSelectionForYear()
      } catch (error) {
        this.$router.push({ name: 'MainPanel' })
      }
    },
    syncWeekSelectionForYear() {
      const weeks = this.payrollWeeks
      if (!weeks.length) {
        this.selectedPayrollWeekKey = ''
        return
      }
      const stillValid = weeks.some((week) => week.key === this.selectedPayrollWeekKey)
      if (!stillValid) {
        this.selectedPayrollWeekKey = weeks[0].key
      }
    },
    buildPayrollWeeks(year) {
      const parsedYear = Number(year)
      if (!Number.isInteger(parsedYear) || parsedYear < 1900) return []
      const startOfYear = new Date(parsedYear, 0, 1)
      const endOfYear = new Date(parsedYear, 11, 31)
      // Week 1 is the Tuesday-Monday week containing January 1; a week
      // belongs to the year its Monday (end date) falls in.
      const offsetBackToTuesday = (startOfYear.getDay() - 2 + 7) % 7
      const weeks = []
      let cursor = new Date(parsedYear, 0, 1 - offsetBackToTuesday)
      let index = 1
      for (;;) {
        const start = new Date(cursor)
        const end = new Date(cursor)
        end.setDate(end.getDate() + 6)
        if (end > endOfYear) break
        weeks.push({
          key: `${parsedYear}-${String(index).padStart(2, '0')}`,
          index,
          startDate: this.toIsoDate(start),
          endDate: this.toIsoDate(end),
        })
        cursor.setDate(cursor.getDate() + 7)
        index += 1
      }
      return weeks
    },
    payrollWeekLabel(week) {
      if (!week) return ''
      return `Semana ${week.index} (${this.formatShortDate(week.startDate)} - ${this.formatShortDate(week.endDate)})`
    },
    toIsoDate(value) {
      const year = value.getFullYear()
      const month = String(value.getMonth() + 1).padStart(2, '0')
      const day = String(value.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    formatShortDate(dateStr) {
      if (!dateStr) return ''
      const [year, month, day] = dateStr.split('-')
      return `${day}/${month}/${year}`
    },
    onFileChange(event) {
      const file = event.target.files && event.target.files[0] ? event.target.files[0] : null
      this.selectedFile = file
      this.errorMessage = ''
      this.missingColumns = []
      this.result = null
    },
    async submit() {
      if (!this.canSubmit || this.uploading) return
      this.errorMessage = ''
      this.missingColumns = []
      this.result = null
      this.uploading = true

      const week = this.selectedPayrollWeek
      const formData = new FormData()
      formData.append('file', this.selectedFile)
      formData.append('company', this.selectedCompany)
      formData.append('week_start_date', week.startDate)
      formData.append('week_end_date', week.endDate)
      formData.append('week_index', week.index)
      formData.append('year', this.selectedYear)

      const apiDirectory = process.env.API_DIRECTORY
      try {
        const { data } = await axios.post(`${apiDirectory}/rrhh/payroll-import`, formData, {
          headers: {
            Authorization: 'Token ' + localStorage.getItem('authToken'),
            'Content-Type': 'multipart/form-data',
          },
        })
        this.result = data
        this.selectedFile = null
        if (this.$refs.fileInput) this.$refs.fileInput.value = ''
        this.fetchExistingImport()
      } catch (error) {
        const data = error.response && error.response.data ? error.response.data : {}
        this.errorMessage = data.error || 'Ocurrio un error al procesar el archivo.'
        this.missingColumns = data.missing_columns || []
      } finally {
        this.uploading = false
      }
    },
    async fetchExistingImport() {
      this.existingImport = null
      if (!this.weekAndCompanySelected) return
      const week = this.selectedPayrollWeek
      const company = this.selectedCompany
      this.checkingExisting = true
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const { data } = await axios.get(`${apiDirectory}/rrhh/payroll-import/lookup`, {
          params: { company, week_start_date: week.startDate },
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        })
        // Ignore the response if the selection changed while awaiting.
        if (company !== this.selectedCompany || !this.selectedPayrollWeek || week.startDate !== this.selectedPayrollWeek.startDate) {
          return
        }
        this.existingImport = data && data.exists ? data.import : null
      } catch (error) {
        this.existingImport = null
      } finally {
        this.checkingExisting = false
      }
    },
    async deleteImport() {
      if (!this.existingImport || this.deleting) return
      const confirmed = window.confirm(
        'Estas seguro de eliminar el archivo cargado? Se eliminaran los datos importados de esta semana.'
      )
      if (!confirmed) return
      this.deleting = true
      const apiDirectory = process.env.API_DIRECTORY
      try {
        await axios.delete(`${apiDirectory}/rrhh/payroll-import/${this.existingImport.import_id}`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        })
        this.existingImport = null
        this.result = null
        this.errorMessage = ''
        this.missingColumns = []
      } catch (error) {
        const data = error.response && error.response.data ? error.response.data : {}
        this.errorMessage = data.error || 'No se pudo eliminar el archivo.'
      } finally {
        this.deleting = false
      }
    },
    async openPreview() {
      if (!this.existingImport || this.previewLoading) return
      this.previewLoading = true
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const { data } = await axios.get(
          `${apiDirectory}/rrhh/payroll-import/${this.existingImport.import_id}`,
          { headers: { Authorization: 'Token ' + localStorage.getItem('authToken') } }
        )
        this.previewData = data
        this.showPreview = true
      } catch (error) {
        const data = error.response && error.response.data ? error.response.data : {}
        this.errorMessage = data.error || 'No se pudo cargar la vista previa.'
      } finally {
        this.previewLoading = false
      }
    },
    closePreview() {
      this.showPreview = false
    },
    formatCell(value) {
      if (value === null || value === undefined) return ''
      return value
    },
    formatDateTime(value) {
      if (!value) return ''
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return value
      return parsed.toLocaleString()
    },
    backMainPanel() {
      this.$router.push({ path: '/main-panel' })
    },
    goBackToMenu() {
      this.$router.push({ path: '/modulo-personal/nomina' })
    },
  },
}
</script>

<style scoped>
.main-panel {
  --bg: #f7f8fb;
  --muted: #667085;
  --text: #0f172a;
  --brand: #ffc434;
  --brand-ink: #3a2a00;
  --primary: #2563eb;
  --ring: rgba(37, 99, 235, 0.25);
  min-height: 100vh;
  background: var(--bg);
  padding: 12vh 24px 48px;
}

.nav-bar-operations {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 10vh;
  background-color: var(--brand);
  z-index: 1000;
  display: flex;
  align-items: center;
  padding-left: 16px;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.05);
}
.logo-button {
  height: 55%;
  object-fit: contain;
  cursor: pointer;
}

.header {
  margin: 12px auto 0;
  max-width: 980px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.header__left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.avatar {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, var(--brand), #ffe188);
  color: var(--brand-ink);
  font-weight: 800;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
}
.greet h1 {
  font-size: 1.15rem;
  color: var(--text);
  margin: 0;
}
.greet h1 span {
  font-weight: 700;
}
.greet .sub {
  margin: 2px 0 0;
  color: var(--muted);
  font-size: 0.9rem;
}
.back-btn {
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  color: var(--text);
  border-radius: 12px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
}
.back-btn:hover {
  background: #f1f5f9;
  transform: translateY(-1px);
}

.wizard {
  margin: 18px auto 0;
  display: grid;
  gap: 16px;
  max-width: 980px;
  width: 100%;
}
.step-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  padding: 16px 18px;
  transition: opacity 0.2s ease;
}
.step-card--disabled {
  opacity: 0.6;
}
.step-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.step-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--primary);
  color: #fff;
  font-weight: 700;
  font-size: 0.9rem;
}
.step-head h2 {
  margin: 0;
  font-size: 1rem;
  color: var(--text);
}
.step-body.row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.85rem;
  color: var(--muted);
}
.field.grow {
  flex: 1;
  min-width: 260px;
}
.field input,
.field select {
  padding: 9px 10px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  font-size: 0.95rem;
  color: var(--text);
  background: #fff;
}

.company-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}
.company-btn {
  padding: 14px 12px;
  border-radius: 12px;
  border: 1.5px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  color: var(--text);
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.15s ease;
}
.company-btn:hover {
  transform: translateY(-1px);
  border-color: var(--primary);
}
.company-btn--active {
  border-color: var(--primary);
  background: rgba(37, 99, 235, 0.1);
  box-shadow: 0 0 0 3px var(--ring);
}

.file-row input[type='file'] {
  display: block;
  margin-bottom: 8px;
}
.hint {
  margin: 6px 0 0;
  font-size: 0.82rem;
  color: var(--muted);
}
.selected-file {
  margin-top: 8px;
  font-size: 0.9rem;
  color: var(--text);
}

.actions {
  display: flex;
  justify-content: flex-end;
}
.submit-btn {
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 12px 22px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}
.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.submit-btn:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 22px rgba(37, 99, 235, 0.35);
}

.error-box {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  border-radius: 12px;
  padding: 14px 16px;
}
.error-box p {
  margin: 6px 0 0;
}
.error-box ul {
  margin: 8px 0 0;
  padding-left: 18px;
}
.success-box {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #166534;
  border-radius: 12px;
  padding: 14px 16px;
}
.success-box p {
  margin: 6px 0 0;
}
.success-box .warn {
  color: #92400e;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.data-chip {
  background: rgba(22, 101, 52, 0.1);
  border: 1px solid rgba(22, 101, 52, 0.3);
  color: #166534;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 0.78rem;
}
.preview-wrap {
  margin-top: 12px;
  overflow-x: auto;
}
.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  background: #fff;
  color: var(--text);
}
.preview-table th,
.preview-table td {
  border: 1px solid rgba(0, 0, 0, 0.1);
  padding: 6px 10px;
  text-align: left;
  white-space: nowrap;
}
.preview-table th {
  background: #f8fafc;
}

.existing-box {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  border-radius: 12px;
  padding: 14px 16px;
}
.existing-box p {
  margin: 0;
}
.existing-meta {
  margin: 10px 0 0;
  padding-left: 18px;
  color: #78350f;
  font-size: 0.9rem;
}
.existing-meta li {
  margin: 2px 0;
}
.existing-actions {
  display: flex;
  gap: 10px;
  margin-top: 14px;
}
.preview-btn,
.delete-btn {
  border: none;
  border-radius: 10px;
  padding: 9px 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}
.preview-btn {
  background: #2563eb;
  color: #fff;
}
.delete-btn {
  background: #dc2626;
  color: #fff;
}
.preview-btn:disabled,
.delete-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.preview-btn:not(:disabled):hover,
.delete-btn:not(:disabled):hover {
  transform: translateY(-1px);
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: grid;
  place-items: center;
  z-index: 2000;
  padding: 24px;
}
.modal {
  background: #fff;
  border-radius: 16px;
  width: min(1100px, 96vw);
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.35);
}
.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}
.modal-head h3 {
  margin: 0;
  font-size: 1.05rem;
  color: var(--text);
}
.modal-close {
  border: none;
  background: transparent;
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
  color: var(--muted);
}
.modal-body {
  padding: 14px 18px;
  overflow: auto;
}

@media (max-width: 720px) {
  .header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
}
</style>
